import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getApiHeaders } from "../../Configurations/Urls";

function extensionOf(name = "") {
  const clean = String(name).split("?")[0].split("#")[0];
  const parts = clean.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function guessMime(name = "", fallback = "") {
  const ext = extensionOf(name);
  const map = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
    txt: "text/plain",
    csv: "text/csv",
    html: "text/html",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  return map[ext] || fallback || "";
}

function previewKind(mime = "", name = "", url = "") {
  const type = String(mime || guessMime(name) || guessMime(url)).toLowerCase();
  const ext = extensionOf(name) || extensionOf(url);
  if (type.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  if (type.includes("pdf") || ext === "pdf") return "pdf";
  if (type.startsWith("text/") || ["txt", "csv", "html"].includes(ext)) return "text";
  if (
    ["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(ext) ||
    type.includes("word") ||
    type.includes("excel") ||
    type.includes("powerpoint") ||
    type.includes("spreadsheet") ||
    type.includes("presentation")
  ) {
    return "office";
  }
  return "other";
}

async function sniffMime(blob, name = "", url = "") {
  try {
    const header = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    if (header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46) {
      return "application/pdf";
    }
    if (header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47) {
      return "image/png";
    }
    if (header[0] === 0xff && header[1] === 0xd8) return "image/jpeg";
    if (header[0] === 0x47 && header[1] === 0x49 && header[2] === 0x46) return "image/gif";
    if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) {
      return "image/webp";
    }
  } catch {
    /* use the declared type */
  }
  const declared = String(blob?.type || "").toLowerCase();
  if (declared && declared !== "application/octet-stream") return declared;
  return guessMime(name) || guessMime(url) || declared || "";
}

function loadPdfJs() {
  if (typeof window !== "undefined" && window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error("PDF viewer failed to load"));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error("PDF viewer failed to load"));
    document.head.appendChild(script);
  });
}

function PdfCanvasPreview({ src }) {
  const holderRef = React.useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    const holder = holderRef.current;

    const run = async () => {
      const pdfjs = await loadPdfJs();
      const pdf = await pdfjs.getDocument(src).promise;
      if (cancelled || !holder) return;
      holder.innerHTML = "";
      const width = Math.max(holder.clientWidth || 320, 280);
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / base.width });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.display = "block";
        canvas.style.marginBottom = "8px";
        canvas.style.background = "#fff";
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        if (cancelled || !holder) return;
        holder.appendChild(canvas);
      }
    };

    run().catch(() => {
      if (!cancelled) setFailed(true);
    });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (failed) {
    return (
      <Box
        component="iframe"
        title="Certificate"
        src={src}
        sx={{ width: "100%", height: "100%", border: 0, bgcolor: "#fff" }}
      />
    );
  }

  return (
    <Box
      ref={holderRef}
      sx={{ width: "100%", height: "100%", overflow: "auto", bgcolor: "#fff" }}
    />
  );
}

export default function FilePreviewDialog({ open, file, fileName = "File", onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [mime, setMime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sourceUrl = file instanceof File ? "" : String(file || "").trim();
  const kind = useMemo(
    () => previewKind(mime, fileName || sourceUrl, sourceUrl),
    [mime, fileName, sourceUrl]
  );
  const officeSrc = sourceUrl
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sourceUrl)}`
    : "";

  useEffect(() => {
    if (!open || file == null || file === "") {
      setBlobUrl("");
      setMime("");
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let createdUrl = "";

    const run = async () => {
      setLoading(true);
      setError("");
      try {
        if (file instanceof File) {
          createdUrl = URL.createObjectURL(file);
          if (!cancelled) {
            setBlobUrl(createdUrl);
            setMime(file.type || guessMime(file.name));
          }
          return;
        }

        const href = String(file);
        const headers = getApiHeaders();
        delete headers["Content-Type"];
        delete headers["content-type"];
        const res = await fetch(href, { headers });
        if (!res.ok) {
          const fail = new Error("Could not load file");
          fail.status = res.status;
          throw fail;
        }
        const blob = await res.blob();
        const blobType = await sniffMime(blob, fileName, href);
        if (blobType.includes("text/html")) {
          throw new Error("Could not load file");
        }
        const typedBlob =
          blobType && blob.type !== blobType
            ? new Blob([await blob.arrayBuffer()], { type: blobType })
            : blob;
        createdUrl = URL.createObjectURL(typedBlob);
        if (!cancelled) {
          setBlobUrl(createdUrl);
          setMime(blobType || guessMime(fileName) || guessMime(href));
        }
      } catch (err) {
        if (!cancelled) {
          const href = file instanceof File ? "" : String(file || "").trim();
          const canEmbed =
            !err?.status &&
            ((/^https?:\/\//i.test(href) || href.startsWith("/")));
          if (canEmbed) {
            setBlobUrl(href);
            setMime(guessMime(href) || guessMime(fileName));
            setError("");
          } else {
            setError("Could not preview this file.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, file, fileName]);

  return (
    <Dialog
      open={Boolean(open) && Boolean(file)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          m: 2,
          width: "min(960px, 96vw)",
          maxWidth: "96vw",
          height: "min(88vh, 900px)",
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#0f172a",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(15, 23, 42, 0.72)" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <VisibilityIcon sx={{ color: "rgba(255,255,255,0.75)", fontSize: 18 }} />
          <Typography
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName || "File preview"}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close preview"
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: 0,
          height: "calc(100% - 48px)",
          p: 1.5,
          overflow: "hidden",
          bgcolor: kind === "image" ? "#0f172a" : "#fff",
        }}
      >
        {loading ? (
          <CircularProgress size={32} />
        ) : error ? (
          <Typography sx={{ color: kind === "image" ? "rgba(255,255,255,0.8)" : "text.secondary" }}>
            {error}
          </Typography>
        ) : kind === "image" && blobUrl ? (
          <Box
            component="img"
            src={blobUrl}
            alt={fileName}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        ) : kind === "pdf" && blobUrl ? (
          <PdfCanvasPreview src={blobUrl} />
        ) : kind === "text" && blobUrl ? (
          <Box
            component="iframe"
            title={fileName}
            src={blobUrl}
            sx={{ width: "100%", height: "100%", border: 0, bgcolor: "#fff" }}
          />
        ) : kind === "office" && officeSrc ? (
          <Box
            component="iframe"
            title={fileName}
            src={officeSrc}
            sx={{ width: "100%", height: "100%", border: 0, bgcolor: "#fff" }}
          />
        ) : (
          <Typography sx={{ color: "text.secondary", textAlign: "center", px: 2 }}>
            This file type cannot be shown in the preview. Use a PDF or image for on-screen viewing.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
